import {
  Alert,
  Box,
  CircularProgress,
  Icons,
  iconSize,
  Link,
  palette,
  Stack,
  StatusChip,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type {
  OfficerCertificationStatus,
  OfficerCertificationSummary,
} from '@munifocus/contracts-gov';
import { formatDate } from '../../lib/relative-time';

/** Expiring is a prompt to renew; expired is a gap in the record. */
const TONE: Record<OfficerCertificationStatus, 'success' | 'warning' | 'rejected'> = {
  active: 'success',
  expiring: 'warning',
  expired: 'rejected',
};

/**
 * An officer's training certifications: course, issue and expiry dates, status
 * and CE credits, each with the public verification link printed on the
 * certificate. Shared by the officer's own settings page and the roster dialog,
 * so both read the same.
 */
export function CertificationList({
  summary,
  isLoading,
  isError,
  emptyLabel,
}: {
  summary: OfficerCertificationSummary | undefined;
  isLoading: boolean;
  isError: boolean;
  emptyLabel: string;
}) {
  const { t, i18n } = useTranslation();

  if (isLoading) return <CircularProgress size={20} />;
  if (isError) return <Alert severity="error">{t('gov.certifications.loadError')}</Alert>;

  const items = summary?.certifications ?? [];
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {items.map((item) => (
        <Stack
          key={item.certificateNumber}
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.75, sm: 2 }}
          sx={{
            py: 1.5,
            borderBottom: `1px solid ${palette.border.line}`,
            '&:first-of-type': { pt: 0 },
            '&:last-of-type': { borderBottom: 'none', pb: 0 },
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {item.courseCode ? `${item.courseCode} · ${item.title}` : item.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {t('gov.certifications.issued', { date: formatDate(item.issuedAt, i18n.language) })}
              {' · '}
              {item.expiresAt
                ? t('gov.certifications.expires', {
                    date: formatDate(item.expiresAt, i18n.language),
                  })
                : t('gov.certifications.noExpiry')}
              {item.ceCredits !== null && item.ceCredits > 0
                ? ` · ${t('gov.certifications.credits', { count: item.ceCredits })}`
                : ''}
            </Typography>
            <Link
              href={item.verificationUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}
            >
              {t('gov.certifications.verify', { number: item.certificateNumber })}
              <Icons.ArrowUpRight size={iconSize.sm} />
            </Link>
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <StatusChip
              status={TONE[item.status]}
              label={t(`gov.certifications.status.${item.status}`)}
            />
          </Box>
        </Stack>
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
        {t('gov.certifications.totalCredits', { count: summary?.totalCeCredits ?? 0 })}
      </Typography>
    </Stack>
  );
}
